import React, { useState } from 'react'
import moment from 'moment'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material'
import ExpandMore from '@mui/icons-material/ExpandMore'
import EventIcon from '@mui/icons-material/Event'
import PersonIcon from '@mui/icons-material/Person'
import WorkspacesIcon from '@mui/icons-material/Workspaces'

const statusMeta = {
  Accepted: {
    color: 'success',
    rail: 'success.main',
    label: 'Accepted',
  },
  Rejected: {
    color: 'error',
    rail: 'error.main',
    label: 'Rejected',
  },
  Pending: {
    color: 'warning',
    rail: 'warning.main',
    label: 'Pending',
  },
}

const MyShiftApplications = ({ applications }) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <Stack gap={1}>
      <Accordion
        disableGutters
        expanded={expanded}
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: 2, overflow: 'hidden', '&:before': { display: 'none' } }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{ display: 'flex' }}
          onClick={() => setExpanded((prev) => !prev)}
        >
          <Typography variant="title2">My Shift Applications</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          {applications.map((application) => {
            const meta = statusMeta[application.status] ?? statusMeta.Pending
            const shift = application.shift

            return (
              <Box
                key={application._id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '4px 1fr', md: '4px 1fr auto' },
                  gap: 1.5,
                  alignItems: 'center',
                  p: 1.5,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  transition: 'background-color .15s ease',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 4,
                    alignSelf: 'stretch',
                    borderRadius: 999,
                    bgcolor: meta.rail,
                  }}
                />

                <Stack gap={0.75} minWidth={0}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    gap={{ xs: 0.25, sm: 1 }}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                  >
                    <Typography fontWeight={700} color="text.primary">
                      {moment(shift.start).format('dddd, Do MMMM YYYY')}
                    </Typography>
                    <Typography color="text.secondary">
                      {moment(shift.start).format('HH:mm')} -{' '}
                      {moment(shift.end).format('HH:mm')}
                    </Typography>
                  </Stack>

                  <Stack direction="row" gap={1} flexWrap="wrap">
                    <Chip
                      size="small"
                      color={meta.color}
                      label={meta.label}
                      sx={{ borderRadius: 1 }}
                    />
                    {shift?.event?.title && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color="anPurple"
                        icon={<WorkspacesIcon />}
                        label={shift.event.title}
                        sx={{ borderRadius: 1, maxWidth: { xs: 240, md: 360 } }}
                      />
                    )}
                    {shift?.clinicalRole && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color="anGreen"
                        icon={<PersonIcon />}
                        label={shift.clinicalRole}
                        sx={{ borderRadius: 1 }}
                      />
                    )}
                  </Stack>

                  {shift?.event?.organisation?.orgName && (
                    <Typography variant="body2" color="text.secondary">
                      {shift.event.organisation.orgName}
                    </Typography>
                  )}
                </Stack>

                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  sx={{ display: { xs: 'none', md: 'flex' } }}
                >
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={<EventIcon />}
                    label={moment(shift.start).fromNow()}
                    sx={{ borderRadius: 1 }}
                  />
                </Stack>
              </Box>
            )
          })}
        </AccordionDetails>
      </Accordion>
    </Stack>
  )
}

export default MyShiftApplications
